import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { calculateAutomationReadiness } from '@/utils/localStorage';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend } from 'recharts';
import { Bot, Target, TrendingUp, Award, AlertTriangle, CheckCircle, XCircle, Info, Zap, Shield, Activity, BarChart3 } from 'lucide-react';

export default function AutomationReadiness() {
  const [data, setData] = useState({ patterns: [], recommendations: [] });
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = () => {
    const result = calculateAutomationReadiness();
    setData(result);
  };
  
  const getScoreGradient = (score) => {
    if (score >= 90) return 'from-success/20 to-success/5';
    if (score >= 70) return 'from-primary/20 to-primary/5';
    if (score >= 50) return 'from-warning/20 to-warning/5';
    return 'from-destructive/20 to-destructive/5';
  };
  
  const getLevelBadge = (level, color) => {
    switch (level) {
      case 'Automation Ready':
        return { icon: CheckCircle, className: 'bg-success/20 text-success border-success/50' };
      case 'Monitor Closely':
        return { icon: Activity, className: 'bg-primary/20 text-primary border-primary/50' };
      case 'Not Ready':
        return { icon: AlertTriangle, className: 'bg-warning/20 text-warning border-warning/50' };
      default:
        return { icon: XCircle, className: 'bg-destructive/20 text-destructive border-destructive/50' };
    }
  };
  
  // Prepare chart data
  const chartData = data.patterns.slice(0, 8).map(p => ({
    name: `${p.asset} ${p.setupType}`.slice(0, 15),
    score: p.readinessScore,
    winRate: parseFloat(p.winRate),
    trades: p.totalTrades
  }));
  
  // Calculate summary stats
  const readyCount = data.patterns.filter(p => p.readinessScore >= 90 && p.meetsMinimum).length;
  const monitorCount = data.patterns.filter(p => p.readinessScore >= 70 && p.readinessScore < 90).length;
  const notReadyCount = data.patterns.filter(p => p.readinessScore >= 50 && p.readinessScore < 70).length;
  const avoidCount = data.patterns.filter(p => p.readinessScore < 50).length;
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <Bot className="w-10 h-10 text-primary" />
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-foreground">
            Automation Readiness
          </h1>
        </div>
        <p className="text-muted-foreground font-medium">
          Determine when patterns are statistically strong enough for automated trading based on historical performance.
        </p>
      </div>
      
      {data.patterns.length > 0 ? (
        <>
          {/* Recommendations Box */}
          {data.recommendations && data.recommendations.length > 0 && (
            <Card className="p-6 border-border/40 bg-gradient-to-br from-primary/5 to-primary/10 mb-6" data-testid="recommendations-box">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-foreground">Automation Insights</h2>
              </div>
              <div className="space-y-3">
                {data.recommendations.map((rec, index) => (
                  <div 
                    key={index}
                    data-testid={`recommendation-${index}`}
                    className={`p-4 rounded-lg border flex items-start space-x-3 ${
                      rec.type === 'success' 
                        ? 'bg-success/10 border-success/30' 
                        : rec.type === 'warning' 
                        ? 'bg-warning/10 border-warning/30'
                        : 'bg-primary/10 border-primary/30'
                    }`}
                  >
                    {rec.type === 'success' ? (
                      <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    ) : rec.type === 'warning' ? (
                      <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                    ) : (
                      <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    )}
                    <p className="text-sm font-medium text-foreground">{rec.message}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
          
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4 border-border/40 bg-success/10" data-testid="ready-count-card">
              <div className="flex items-center space-x-2 mb-1">
                <CheckCircle className="w-4 h-4 text-success" />
                <p className="text-xs text-success font-semibold">AUTOMATION READY</p>
              </div>
              <p className="text-3xl font-black font-mono text-success">{readyCount}</p>
              <p className="text-xs text-muted-foreground">90-100 score</p>
            </Card>
            
            <Card className="p-4 border-border/40 bg-primary/10" data-testid="monitor-count-card">
              <div className="flex items-center space-x-2 mb-1">
                <Activity className="w-4 h-4 text-primary" />
                <p className="text-xs text-primary font-semibold">MONITOR CLOSELY</p>
              </div>
              <p className="text-3xl font-black font-mono text-primary">{monitorCount}</p>
              <p className="text-xs text-muted-foreground">70-89 score</p>
            </Card>
            
            <Card className="p-4 border-border/40 bg-warning/10" data-testid="notready-count-card">
              <div className="flex items-center space-x-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-warning" />
                <p className="text-xs text-warning font-semibold">NOT READY</p>
              </div>
              <p className="text-3xl font-black font-mono text-warning">{notReadyCount}</p>
              <p className="text-xs text-muted-foreground">50-69 score</p>
            </Card>
            
            <Card className="p-4 border-border/40 bg-destructive/10" data-testid="avoid-count-card">
              <div className="flex items-center space-x-2 mb-1">
                <XCircle className="w-4 h-4 text-destructive" />
                <p className="text-xs text-destructive font-semibold">AVOID</p>
              </div>
              <p className="text-3xl font-black font-mono text-destructive">{avoidCount}</p>
              <p className="text-xs text-muted-foreground">0-49 score</p>
            </Card>
          </div>
          
          {/* Scoring Criteria Info */}
          <Card className="p-6 border-border/40 bg-card mb-6" data-testid="scoring-criteria">
            <div className="flex items-center space-x-2 mb-4">
              <Shield className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-bold text-foreground">Minimum Requirements for "Automation Ready"</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-secondary/30 border border-border/40">
                <p className="text-xs text-muted-foreground mb-1">Sample Size</p>
                <p className="text-lg font-bold">50+ trades</p>
              </div>
              <div className="p-3 rounded-lg bg-secondary/30 border border-border/40">
                <p className="text-xs text-muted-foreground mb-1">Win Rate</p>
                <p className="text-lg font-bold">≥ 55%</p>
              </div>
              <div className="p-3 rounded-lg bg-secondary/30 border border-border/40">
                <p className="text-xs text-muted-foreground mb-1">Expectancy</p>
                <p className="text-lg font-bold">Positive ({">"} 0R)</p>
              </div>
              <div className="p-3 rounded-lg bg-secondary/30 border border-border/40">
                <p className="text-xs text-muted-foreground mb-1">Stability</p>
                <p className="text-lg font-bold">No decline</p>
              </div>
            </div>
          </Card>
          
          {/* Readiness Score Chart */}
          {chartData.length > 0 && (
            <Card className="p-6 border-border/40 bg-card mb-6" data-testid="readiness-chart">
              <h2 className="text-xl font-bold text-foreground mb-4">Pattern Readiness Scores</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis type="number" domain={[0, 100]} stroke="#A1A1AA" />
                  <YAxis dataKey="name" type="category" stroke="#A1A1AA" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                    labelStyle={{ color: '#FAFAFA' }}
                    formatter={(value, name) => {
                      if (name === 'score') return [`${value}/100`, 'Readiness Score'];
                      return [value, name];
                    }}
                  />
                  <Bar dataKey="score" name="Readiness Score" radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, index) => {
                      let fill = '#EF4444';
                      if (entry.score >= 90) fill = '#10B981';
                      else if (entry.score >= 70) fill = '#3B82F6';
                      else if (entry.score >= 50) fill = '#F59E0B';
                      return <Cell key={`cell-${index}`} fill={fill} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
          
          {/* Pattern Breakdown Table */}
          <Card className="p-6 border-border/40 bg-card" data-testid="patterns-table">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-bold text-foreground">Pattern Analysis</h2>
              </div>
              <Button onClick={loadData} variant="ghost" size="sm" data-testid="refresh-btn">
                Refresh
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border/40">
                  <tr className="text-left">
                    <th className="p-4 font-bold text-sm text-muted-foreground">Pattern</th>
                    <th className="p-4 font-bold text-sm text-muted-foreground">Trades</th>
                    <th className="p-4 font-bold text-sm text-muted-foreground">Win Rate</th>
                    <th className="p-4 font-bold text-sm text-muted-foreground">Expectancy</th>
                    <th className="p-4 font-bold text-sm text-muted-foreground">Consistency</th>
                    <th className="p-4 font-bold text-sm text-muted-foreground">Readiness Score</th>
                    <th className="p-4 font-bold text-sm text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.patterns.map((pattern, index) => {
                    const badge = getLevelBadge(pattern.readinessLevel, pattern.readinessColor);
                    const BadgeIcon = badge.icon;
                    return (
                      <tr 
                        key={index} 
                        className={`border-b border-border/20 hover:bg-accent/50 transition-colors ${
                          pattern.readinessScore >= 90 && pattern.meetsMinimum ? 'bg-success/5' : ''
                        }`}
                        data-testid={`pattern-row-${index}`}
                      >
                        <td className="p-4">
                          <div>
                            <span className="font-bold text-foreground">{pattern.asset}</span>
                            <p className="text-xs text-muted-foreground capitalize">{pattern.setupType} ({pattern.timeframe})</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <span className="font-mono font-bold">{pattern.totalTrades}</span>
                            {pattern.totalTrades < 50 && (
                              <span className="text-xs text-warning">({50 - pattern.totalTrades} needed)</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <span 
                            className="font-mono font-bold"
                            style={{ color: parseFloat(pattern.winRate) >= 55 ? '#10B981' : parseFloat(pattern.winRate) >= 45 ? '#F59E0B' : '#EF4444' }}
                          >
                            {pattern.winRate}%
                          </span>
                        </td>
                        <td className="p-4">
                          <span 
                            className="font-mono font-bold"
                            style={{ color: parseFloat(pattern.expectancy) > 0 ? '#10B981' : '#EF4444' }}
                          >
                            {pattern.expectancy}R
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <Progress 
                              value={parseFloat(pattern.consistencyScore)} 
                              className="w-16 h-2"
                            />
                            <span className="text-xs font-mono">{pattern.consistencyScore}%</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className={`flex items-center space-x-2 p-2 rounded-lg bg-gradient-to-r ${getScoreGradient(pattern.readinessScore)}`}>
                            <span 
                              className="text-2xl font-black font-mono"
                              style={{ color: pattern.readinessColor }}
                            >
                              {pattern.readinessScore}
                            </span>
                            <span className="text-xs text-muted-foreground">/100</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className={`px-3 py-1.5 rounded-md text-xs font-bold border inline-flex items-center space-x-1.5 ${badge.className}`}>
                            <BadgeIcon className="w-3.5 h-3.5" />
                            <span>{pattern.readinessLevel}</span>
                          </div>
                          {pattern.failedCriteria.length > 0 && pattern.readinessScore < 90 && (
                            <p className="text-[10px] text-muted-foreground mt-1 max-w-[150px]">
                              {pattern.failedCriteria[0]}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : (
        <Card className="p-12 border-border/40 bg-card text-center" data-testid="empty-state">
          <Bot className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-20" />
          <p className="text-muted-foreground font-medium mb-2">No pattern data available</p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Start logging trades in your Trade Journal with consistent setup types, timeframes, and assets to build pattern history for automation analysis.
          </p>
        </Card>
      )}
    </div>
  );
}
